/**
 * @file App.js
 * @description Main component of the application.
 **/
import { useEffect, useState, useRef } from 'react';
import './App.css';
import Navbar from './components/navbar/Navbar';
import AppRoutes from './AppRoutes';
import Header from './components/header/Header';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    StylingContext,
    ModalProvider,
    ThemeProvider,
    UploadProvider,
    useAuthContext,
} from './store/Contexts';
import { Modal } from './components/ui/modals/Modals';
import UploadTracker from './components/ui/upload-tracker/UploadTracker';
import { setLogoutFunction } from './services/httpService';
import ProfileComponent from './components/branding/ProfileComponent';
import { NavLink, useLocation } from 'react-router-dom';
import { capitalizeFirstLetter } from './utils/utils';
import NotificationPopup from './components/NotificationPopup';
import useNotifications from './hooks/useNotifications';
import { isPWA, isMobileDevice } from './utils/deviceCheck';
import InstallationPopup from './components/InstallationPopup';
import TokenRefreshManager from './components/auth/TokenRefreshManager';
import Toast from './components/ui/toasts/Toast';
import { useIndividualHasBenefitAccess } from './hooks/useIndividualHasBenefitAccess.js';
import { useIndividualViewRouteGuard } from './hooks/useIndividualViewRouteGuard.js';
import { usePushNotificationNavigation } from './hooks/usePushNotificationNavigation.js';
import { LogEventType, SecurityAttributes, UserType } from './utils/data/enums';
import { logEvent } from './services/loggingService';
import { ROUTES } from './constants/routes';
import { getText } from './i18n';
import { getActiveBottomNavTab } from './utils/bottomNavTabs';
function App() {
    const [currentStyling, setCurrentStyling] = useState({
        mainClass: '',
        navbarContentClass: '',
        navbarIconClass: '',
        navbarIMGClass: '',
        navbarLinkClass: '',
        navbarSidenavClass: '',
        navbarTextClass: '',
    });

    const [headerVisible, setHeaderVisible] = useState(true);
    const lastScrollY = useRef(0);

    const location = useLocation();
    const {
        logout,
        isLoggedIn,
        user,
        hasSecurityAttribute,
        adminView,
        menuLabels,
        displayIndividualAssetMenu,
        menuLabelsLoaded,
    } = useAuthContext();
    const isLimitedUser = user?.jwt?.type === UserType.LimitedUser;
    const activeTab = getActiveBottomNavTab(location.pathname);
    const [isProfileVisible] = useState(false);
    const deployedEnvName = process.env.REACT_APP_DEPLOYED_ENV_NAME || 'Local';
    const showIndividualBenefits = useIndividualHasBenefitAccess();

    useIndividualViewRouteGuard();

    usePushNotificationNavigation();

    useEffect(() => {
        setLogoutFunction(logout);
    }, [logout]);

    const {
        requestNotificationPermission,
        isPushSupported,
        syncFCMRegistration,
    } = useNotifications();
    const [showNotificationPopup, setShowNotificationPopup] = useState(false);
    const [showInstallationPopup, setShowInstallationPopup] = useState(false);
    const [isMobileSession, setIsMobileSession] = useState(false);

    useEffect(() => {
        if (!isLoggedIn || !isPushSupported) {
            return undefined;
        }

        let disposed = false;
        let synchronizationInProgress = null;
        let synchronizedThisSession = false;

        const synchronizePushRegistration = async () => {
            if (
                disposed ||
                synchronizedThisSession ||
                !('Notification' in window) ||
                Notification.permission !== 'granted'
            ) {
                return;
            }
            //Don't start firebase while app is it in background or closed
            if (document.visibilityState !== 'visible') {
                return;
            }

            //On startup, app to foreground or any visibility change, only trigger sync if not already in progress
            if (synchronizationInProgress) {
                return;
            }

            synchronizationInProgress = (async () => {
                const installedPWA = await isPWA();
                if (!installedPWA || disposed) {
                    return;
                }

                const token = await syncFCMRegistration();
                if (token && !disposed) {
                    synchronizedThisSession = true;
                }
            })()
                .catch(error => {
                    if (!disposed) {
                        console.error('Error syncing FCM registration:', error);
                    }
                })
                .finally(() => {
                    synchronizationInProgress = null;
                });
        };

        //On opening app when already logged in, on authenticating, on new login trigger:
        synchronizePushRegistration();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                synchronizePushRegistration();
            }
        };

        // If page is frozen and then resumed, re-sync the FCM registration
        const handlePageShow = () => {
            synchronizePushRegistration();
        };

        //Retry sync if it failed because device was offline
        const handleOnline = () => {
            synchronizePushRegistration();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('online', handleOnline);

        return () => {
            disposed = true;
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('online', handleOnline);
        };
    }, [isLoggedIn, isPushSupported, syncFCMRegistration]);

    useEffect(() => {
        let isPhysicalMobileDevice = false;

        const updateMobileState = () => {
            const isMobileViewport = window.innerWidth <= 768;
            setIsMobileSession(isPhysicalMobileDevice || isMobileViewport);
        };

        const detectMobileSession = async () => {
            isPhysicalMobileDevice = await isMobileDevice();
            updateMobileState();
        };

        detectMobileSession();

        window.addEventListener('resize', updateMobileState);

        return () => {
            window.removeEventListener('resize', updateMobileState);
        };
    }, []);

    const isClientAdminUser = user?.jwt?.type <= UserType.ClientAdmin;

    const isImpersonating =
        window.localStorage.getItem('impersonation_token') !== null;

    const useDesktopCanvasOnMobile =
        isMobileSession &&
        isLoggedIn &&
        adminView &&
        isClientAdminUser &&
        !isImpersonating;

    const showBottomNav = isLoggedIn && isMobileSession && !adminView;

    const handleRequestNotification = async () => {
        try {
            await requestNotificationPermission();
        } catch (error) {
            console.error('Failed to request notification permission:', error);
            if (
                'Notification' in window &&
                Notification.permission === 'denied'
            ) {
                Toast.error(getText('msg_f2afbd3be48f'));
            } else {
                Toast.error(getText('msg_6a0a1724566b'));
            }
        } finally {
            handleCloseNotificationPopup();
        }
    };

    useEffect(() => {
        const evaluateInstallationPopupConditions = async () => {
            if (!isLoggedIn) {
                return;
            }

            let isUserFirstAccess = false;
            try {
                isUserFirstAccess =
                    localStorage.getItem('installPopupShown') !== 'true';
            } catch {
                isUserFirstAccess = true;
            }

            const isInMobileDevice = await isMobileDevice();
            const isInPWA = await isPWA();

            if (isUserFirstAccess && isInMobileDevice && !isInPWA) {
                setShowInstallationPopup(true);
            }
        };

        evaluateInstallationPopupConditions();
    }, [isLoggedIn]);

    useEffect(() => {
        const evaluateNotificationPopupConditions = async () => {
            const isInPWA = await isPWA();
            const canRequestPermission =
                'Notification' in window &&
                Notification.permission === 'default';

            setShowNotificationPopup(
                isInPWA &&
                    isLoggedIn &&
                    isPushSupported &&
                    canRequestPermission,
            );
        };

        void evaluateNotificationPopupConditions();
    }, [isLoggedIn, isPushSupported]);

    const handleCloseNotificationPopup = () => {
        setShowNotificationPopup(false);
    };

    const handleCloseInstallationPopup = () => {
        setShowInstallationPopup(false);
        try {
            localStorage.setItem('installPopupShown', 'true');
        } catch (e) {
            console.warn('Failed to set installPopupShown', e);
        }
    };

    const formatEnvName = envName => {
        return envName.length < 3
            ? envName.toUpperCase()
            : capitalizeFirstLetter(envName);
    };

    useEffect(() => {
        document.documentElement.style.setProperty('--header-height', '80px');
        document.documentElement.style.setProperty(
            '--header-content-translateY',
            '0px',
        );
    }, []);

    useEffect(() => {
        const mainElement = document.querySelector('.main');
        if (!mainElement) return;

        const onScroll = () => {
            const isEnabled = window.innerWidth < 768;
            const currentY = mainElement.scrollTop;

            if (!isEnabled) {
                lastScrollY.current = currentY;
                if (!headerVisible) {
                    setHeaderVisible(true);
                    document.documentElement.style.setProperty(
                        '--header-height',
                        '80px',
                    );
                    document.documentElement.style.setProperty(
                        '--header-content-translateY',
                        '0px',
                    );
                }
                return;
            }

            const DEAD_ZONE_PX = 20;
            const maxY = mainElement.scrollHeight - mainElement.clientHeight;

            const inTopDeadZone = currentY <= DEAD_ZONE_PX;
            const inBottomDeadZone = currentY >= maxY - DEAD_ZONE_PX;

            if (inTopDeadZone) {
                lastScrollY.current = currentY;
                if (!headerVisible) {
                    setHeaderVisible(true);
                    document.documentElement.style.setProperty(
                        '--header-height',
                        '80px',
                    );
                    document.documentElement.style.setProperty(
                        '--header-content-translateY',
                        '0px',
                    );
                }
                return;
            }

            if (inBottomDeadZone) {
                lastScrollY.current = currentY;
                if (headerVisible) {
                    setHeaderVisible(false);
                    document.documentElement.style.setProperty(
                        '--header-height',
                        '0px',
                    );
                    document.documentElement.style.setProperty(
                        '--header-content-translateY',
                        '-40px',
                    );
                }
                return;
            }

            const delta = currentY - lastScrollY.current;
            let nextVisible = headerVisible;

            if (delta > 0) {
                // Scrolling down
                nextVisible = false;
            } else if (delta < 0) {
                // Scrolling up
                nextVisible = true;
            }

            if (nextVisible !== headerVisible) {
                setHeaderVisible(nextVisible);
                document.documentElement.style.setProperty(
                    '--header-height',
                    nextVisible ? '80px' : '0px',
                );
                document.documentElement.style.setProperty(
                    '--header-content-translateY',
                    nextVisible ? '0px' : '-40px',
                );
            }

            lastScrollY.current = currentY;
        };

        mainElement.addEventListener('scroll', onScroll, { passive: true });
        lastScrollY.current = mainElement.scrollTop;

        return () => {
            mainElement.removeEventListener('scroll', onScroll);
        };
    }, [headerVisible]);

    return (
        <>
            <ThemeProvider>
                <StylingContext.Provider
                    value={{ currentStyling, setCurrentStyling }}
                >
                    <ModalProvider>
                        <UploadProvider>
                            {isLoggedIn && <TokenRefreshManager />}
                            {isLoggedIn && <UploadTracker />}
                            {showNotificationPopup && (
                                <NotificationPopup
                                    onConfirm={handleRequestNotification}
                                    onClose={handleCloseNotificationPopup}
                                />
                            )}
                            {showInstallationPopup && (
                                <InstallationPopup
                                    onClose={handleCloseInstallationPopup}
                                />
                            )}
                            <Modal />
                            {isLoggedIn &&
                                !deployedEnvName
                                    .toUpperCase()
                                    .includes('PROD') && (
                                    <div className="header-env">
                                        {`${formatEnvName(deployedEnvName)} Environment`}
                                    </div>
                                )}
                            <Header visible={headerVisible} />
                            <div
                                className={`middle ${
                                    useDesktopCanvasOnMobile
                                        ? 'mobile-admin-display'
                                        : ''
                                }`}
                            >
                                <div
                                    className={`absolute-flex ${adminView ? 'admin-view-layout' : ''} ${
                                        useDesktopCanvasOnMobile
                                            ? 'mobile-admin-display'
                                            : ''
                                    }`}
                                >
                                    {isLoggedIn && <Navbar />}
                                    <ToastContainer
                                        className="custom-toast-container"
                                        autoClose={5000}
                                    />
                                    <div
                                        className={`main ${currentStyling.mainClass} ${
                                            showBottomNav
                                                ? 'mobileNavBarPadding'
                                                : ''
                                        } ${
                                            useDesktopCanvasOnMobile
                                                ? 'mobile-admin-display'
                                                : ''
                                        }`}
                                    >
                                        <AppRoutes />
                                    </div>
                                </div>
                            </div>

                            {showBottomNav && (
                                <div className="bottom-tab-bar">
                                    {!(
                                        isLimitedUser &&
                                        !hasSecurityAttribute([
                                            SecurityAttributes.UserAccessPortals,
                                        ])
                                    ) && (
                                        <NavLink to={ROUTES.ROOT}>
                                            <div
                                                className={`tab-item ${
                                                    activeTab === 'home'
                                                        ? 'active'
                                                        : ''
                                                }`}
                                            >
                                                <i
                                                    className={`bi bi-house-door-fill logo ${
                                                        activeTab === 'home'
                                                            ? 'active-logo'
                                                            : 'inactive-logo'
                                                    }`}
                                                />
                                                <span
                                                    className={`tab-text ${
                                                        activeTab === 'home'
                                                            ? 'bold-text'
                                                            : ''
                                                    }`}
                                                >
                                                    {getText(
                                                        'msg_70f8bb9a8a53',
                                                    )}
                                                </span>
                                            </div>
                                        </NavLink>
                                    )}
                                    {displayIndividualAssetMenu && (
                                        <NavLink to={ROUTES.MY_ASSETS}>
                                            <div
                                                className={`tab-item ${
                                                    activeTab === 'my-assets'
                                                        ? 'active'
                                                        : ''
                                                }`}
                                            >
                                                <i
                                                    className={`bi bi-book logo ${
                                                        activeTab ===
                                                        'my-assets'
                                                            ? 'active-logo'
                                                            : 'inactive-logo'
                                                    }`}
                                                />
                                                <span
                                                    className={`tab-text ${
                                                        activeTab ===
                                                        'my-assets'
                                                            ? 'bold-text'
                                                            : ''
                                                    }`}
                                                >
                                                    {menuLabelsLoaded
                                                        ? menuLabels.assets
                                                        : ''}
                                                </span>
                                            </div>
                                        </NavLink>
                                    )}

                                    {showIndividualBenefits &&
                                        !(
                                            isLimitedUser &&
                                            !hasSecurityAttribute([
                                                SecurityAttributes.UserAccessBenefits,
                                            ])
                                        ) && (
                                            <NavLink to={ROUTES.MY_BENEFITS}>
                                                <div
                                                    className={`tab-item ${
                                                        activeTab ===
                                                        'my-benefits'
                                                            ? 'active'
                                                            : ''
                                                    }`}
                                                >
                                                    <i
                                                        className={`bi bi-heart-pulse logo ${
                                                            activeTab ===
                                                            'my-benefits'
                                                                ? 'active-logo'
                                                                : 'inactive-logo'
                                                        }`}
                                                    />
                                                    <span
                                                        className={`tab-text ${
                                                            activeTab ===
                                                            'my-benefits'
                                                                ? 'bold-text'
                                                                : ''
                                                        }`}
                                                    >
                                                        {menuLabelsLoaded
                                                            ? menuLabels.benefits
                                                            : ''}
                                                    </span>
                                                </div>
                                            </NavLink>
                                        )}

                                    {!isLimitedUser && (
                                        <NavLink to={ROUTES.MY_MESSAGES}>
                                            <div
                                                className={`tab-item ${
                                                    activeTab === 'my-messages'
                                                        ? 'active'
                                                        : ''
                                                }`}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    className={`logo ${
                                                        activeTab ===
                                                        'my-messages'
                                                            ? 'active-logo'
                                                            : 'inactive-logo'
                                                    }`}
                                                >
                                                    <path d="M0 2C0 0.895431 0.895431 0 2 0H14C15.1046 0 16 0.89543 16 2V10C16 11.1046 15.1046 12 14 12H2C0.895431 12 0 11.1046 0 10V2ZM2 1C1.44772 1 1 1.44772 1 2V2.2169L8 6.4169L15 2.2169V2C15 1.44772 14.5523 1 14 1H2ZM15 3.3831L10.2919 6.20794L15 9.10522V3.3831ZM14.9662 10.2586L9.32583 6.7876L8 7.5831L6.67417 6.7876L1.03376 10.2586C1.14774 10.6855 1.53715 11 2 11H14C14.4628 11 14.8523 10.6855 14.9662 10.2586ZM1 9.10522L5.70808 6.20794L1 3.3831V9.10522Z" />
                                                </svg>
                                                <span
                                                    className={`tab-text ${
                                                        activeTab ===
                                                        'my-messages'
                                                            ? 'bold-text'
                                                            : ''
                                                    }`}
                                                >
                                                    {menuLabelsLoaded
                                                        ? menuLabels.messages
                                                        : ''}
                                                </span>
                                            </div>
                                        </NavLink>
                                    )}
                                </div>
                            )}
                            {isLoggedIn &&
                                window.localStorage.getItem(
                                    'impersonation_token',
                                ) != null && (
                                    <div
                                        className={`footer-impersonate ${showBottomNav ? 'footer-impersonate-mobile-view' : ''}`}
                                    >
                                        <span>
                                            {getText('msg_c677a60532f1')}
                                        </span>
                                        <button
                                            className="footer-exit-button"
                                            onClick={() => {
                                                localStorage.removeItem(
                                                    'impersonation_token',
                                                );
                                                localStorage.setItem(
                                                    'adminView',
                                                    'true',
                                                );
                                                logEvent(
                                                    LogEventType.ImpersonationEnded,
                                                    `Impersonation ended`,
                                                );
                                                window.location.href =
                                                    ROUTES.INDIVIDUALS;
                                            }}
                                            type="button"
                                        >
                                            {getText('msg_bb1b38004d2e')}
                                        </button>
                                    </div>
                                )}
                        </UploadProvider>
                    </ModalProvider>
                </StylingContext.Provider>
            </ThemeProvider>
            {isProfileVisible && <ProfileComponent />}
        </>
    );
}
export default App;
